/**
 * @module Select
 *
 * Dropdown select menu for single value selection. Built on Radix UI Select primitive.
 * Includes keyboard navigation, typeahead, and customizable styling.
 *
 * @see {@link https://ui.shadcn.com/docs/components/select Shadcn Select}
 * @see {@link https://www.radix-ui.com/primitives/docs/components/select Radix Select}
 *
 * @example
 * // Basic select
 * <Select>
 *   <SelectTrigger className="w-48">
 *     <SelectValue placeholder="Select option" />
 *   </SelectTrigger>
 *   <SelectContent>
 *     <SelectItem value="a">Option A</SelectItem>
 *     <SelectItem value="b">Option B</SelectItem>
 *   </SelectContent>
 * </Select>
 *
 * @example
 * // Grouped options
 * <SelectContent>
 *   <SelectGroup>
 *     <SelectLabel>Fruits</SelectLabel>
 *     <SelectItem value="apple">Apple</SelectItem>
 *     <SelectItem value="banana">Banana</SelectItem>
 *   </SelectGroup>
 *   <SelectSeparator />
 *   <SelectGroup>
 *     <SelectLabel>Vegetables</SelectLabel>
 *     <SelectItem value="carrot">Carrot</SelectItem>
 *   </SelectGroup>
 * </SelectContent>
 */
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { Select as SelectPrimitive } from "radix-ui";
import type * as React from "react";
import type { Ref } from "react";
import { cn } from "../lib/utils";
import { dropdownChevronVariants, dropdownTriggerVariants } from "./dropdown-trigger";

/** Root component that manages select state. */
const Select = SelectPrimitive.Root;

/** Container for grouping related select items with a label. */
const SelectGroup = SelectPrimitive.Group;

/** Displays the selected value or placeholder text. */
const SelectValue = SelectPrimitive.Value;

/**
 * Button that opens the select dropdown.
 * @param icon - Custom dropdown icon (defaults to ChevronDown)
 */
interface SelectTriggerProps extends Omit<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>, "ref"> {
  icon?: React.ReactNode;
  ref?: Ref<React.ElementRef<typeof SelectPrimitive.Trigger>>;
}
const SelectTrigger = ({
  className,
  children,
  icon,
  ref,
  ...props
}: SelectTriggerProps) => <SelectPrimitive.Trigger className={cn(dropdownTriggerVariants(), "group relative flex items-center gap-2 justify-between [&>span]:line-clamp-1", className)} ref={ref} {...props} data-sentry-element="SelectPrimitive.Trigger" data-sentry-component="SelectTrigger" data-sentry-source-file="select.tsx">
    {children}
    <SelectPrimitive.Icon asChild data-sentry-element="SelectPrimitive.Icon" data-sentry-source-file="select.tsx">
      {icon ?? <ChevronDown aria-hidden="true" className={cn(dropdownChevronVariants({
      context: "bordered"
    }))} />}
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>;
interface SelectScrollUpButtonProps extends Omit<React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>, "ref"> {
  ref?: Ref<React.ElementRef<typeof SelectPrimitive.ScrollUpButton>>;
}
const SelectScrollUpButton = ({
  className,
  ref,
  ...props
}: SelectScrollUpButtonProps) => <SelectPrimitive.ScrollUpButton className={cn("flex cursor-default items-center justify-center py-1", className)} ref={ref} {...props} data-sentry-element="SelectPrimitive.ScrollUpButton" data-sentry-component="SelectScrollUpButton" data-sentry-source-file="select.tsx">
    <ChevronUp className="h-5 w-5" data-sentry-element="ChevronUp" data-sentry-source-file="select.tsx" />
  </SelectPrimitive.ScrollUpButton>;
interface SelectScrollDownButtonProps extends Omit<React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>, "ref"> {
  ref?: Ref<React.ElementRef<typeof SelectPrimitive.ScrollDownButton>>;
}
const SelectScrollDownButton = ({
  className,
  ref,
  ...props
}: SelectScrollDownButtonProps) => <SelectPrimitive.ScrollDownButton className={cn("flex cursor-default items-center justify-center py-1", className)} ref={ref} {...props} data-sentry-element="SelectPrimitive.ScrollDownButton" data-sentry-component="SelectScrollDownButton" data-sentry-source-file="select.tsx">
    <ChevronDown className="h-4 w-4" data-sentry-element="ChevronDown" data-sentry-source-file="select.tsx" />
  </SelectPrimitive.ScrollDownButton>;
interface SelectContentProps extends Omit<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>, "ref"> {
  ref?: Ref<React.ElementRef<typeof SelectPrimitive.Content>>;
}
const SelectContent = ({
  className,
  children,
  position,
  ref,
  ...props
}: SelectContentProps) => <SelectPrimitive.Portal data-sentry-element="SelectPrimitive.Portal" data-sentry-component="SelectContent" data-sentry-source-file="select.tsx">
    <SelectPrimitive.Content className={cn("data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-96 min-w-[8rem] rounded-lg border border-gray-200 border-solid bg-white text-gray-950 shadow-[0px_2px_4px_0px_rgba(0,_0,_0,_0.15)] data-[state=closed]:animate-out data-[state=open]:animate-in dark:border-gray-800 dark:bg-gray-950 dark:text-white", position === "popper" && "data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1", className)} position={position} ref={ref} {...props} data-sentry-element="SelectPrimitive.Content" data-sentry-source-file="select.tsx">
      <SelectScrollUpButton data-sentry-element="SelectScrollUpButton" data-sentry-source-file="select.tsx" />
      <SelectPrimitive.Viewport className={cn("p-1", position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]")} data-sentry-element="SelectPrimitive.Viewport" data-sentry-source-file="select.tsx">
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton data-sentry-element="SelectScrollDownButton" data-sentry-source-file="select.tsx" />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>;
interface SelectContentPopperProps extends Omit<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>, "ref"> {
  ref?: Ref<React.ElementRef<typeof SelectPrimitive.Content>>;
}
const SelectContentPopper = ({
  className,
  children,
  position = "popper",
  ref,
  ...props
}: SelectContentPopperProps) => <SelectPrimitive.Portal data-sentry-element="SelectPrimitive.Portal" data-sentry-component="SelectContentPopper" data-sentry-source-file="select.tsx">
    <SelectPrimitive.Content className={cn("data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-lg border border-gray-200 border-solid bg-white text-gray-950 shadow-100 data-[state=closed]:animate-out data-[state=open]:animate-in dark:border-gray-800 dark:bg-gray-950 dark:text-white", position === "popper" && "data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1", className)} position={position} ref={ref} {...props} data-sentry-element="SelectPrimitive.Content" data-sentry-source-file="select.tsx">
      <SelectScrollUpButton data-sentry-element="SelectScrollUpButton" data-sentry-source-file="select.tsx" />
      <SelectPrimitive.Viewport className={cn("p-1", position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]")} data-sentry-element="SelectPrimitive.Viewport" data-sentry-source-file="select.tsx">
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton data-sentry-element="SelectScrollDownButton" data-sentry-source-file="select.tsx" />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>;
interface SelectLabelProps extends Omit<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>, "ref"> {
  ref?: Ref<React.ElementRef<typeof SelectPrimitive.Label>>;
}
const SelectLabel = ({
  className,
  ref,
  ...props
}: SelectLabelProps) => <SelectPrimitive.Label className={cn("py-1.5 pr-2 pl-8 font-semibold text-base", className)} ref={ref} {...props} data-sentry-element="SelectPrimitive.Label" data-sentry-component="SelectLabel" data-sentry-source-file="select.tsx" />;
interface SelectItemProps extends Omit<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>, "ref"> {
  hideCheck?: boolean;
  ref?: Ref<React.ElementRef<typeof SelectPrimitive.Item>>;
}
const SelectItem = ({
  className,
  children,
  hideCheck,
  ref,
  ...props
}: SelectItemProps) => <SelectPrimitive.Item className={cn("relative flex w-full cursor-default select-none items-center rounded-xs py-1.5 pr-2 pl-8 text-base outline-hidden focus:bg-gray-50 focus:text-gray-950 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 dark:focus:bg-gray-900 dark:focus:text-white", className)} ref={ref} {...props} data-sentry-element="SelectPrimitive.Item" data-sentry-component="SelectItem" data-sentry-source-file="select.tsx">
    {hideCheck ? null : <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>}

    <SelectPrimitive.ItemText data-sentry-element="SelectPrimitive.ItemText" data-sentry-source-file="select.tsx">{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>;
interface SelectSeparatorProps extends Omit<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>, "ref"> {
  ref?: Ref<React.ElementRef<typeof SelectPrimitive.Separator>>;
}
const SelectSeparator = ({
  className,
  ref,
  ...props
}: SelectSeparatorProps) => <SelectPrimitive.Separator className={cn("-mx-1 my-1 h-px bg-gray-150 dark:bg-gray-800", className)} ref={ref} {...props} data-sentry-element="SelectPrimitive.Separator" data-sentry-component="SelectSeparator" data-sentry-source-file="select.tsx" />;
export { Select, SelectContent, SelectContentPopper, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue };